'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import Link from 'next/link'
import { useRouter, useSearchParams } from 'next/navigation'
import { useState, useTransition } from 'react'
import { useForm } from 'react-hook-form'

import { loginUser } from '@/actions/user-auth'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { loginFormSchema, type LoginFormValues } from '@/lib/auth/schemas'

export function LoginForm() {
  const router = useRouter()
  const searchParams = useSearchParams()
  const redirectTo = searchParams.get('vrati') ?? '/'
  const [serverError, setServerError] = useState<string | null>(null)
  const [isPending, startTransition] = useTransition()

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginFormSchema),
    defaultValues: { email: '', password: '' },
  })

  function onSubmit(values: LoginFormValues) {
    setServerError(null)
    const formData = new FormData()
    formData.set('email', values.email)
    formData.set('password', values.password)
    startTransition(async () => {
      const result = await loginUser(formData)
      if (result.ok) {
        router.push(redirectTo)
        router.refresh()
      } else {
        setServerError(result.message ?? 'Greška pri prijavi')
      }
    })
  }

  const disabled = isSubmitting || isPending

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4" noValidate>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="email">Email adresa</Label>
        <input
          id="email"
          type="email"
          autoComplete="email"
          className="border-input bg-background focus-visible:ring-ring rounded-md border px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2"
          aria-invalid={Boolean(errors.email)}
          aria-describedby={errors.email ? 'email-error' : undefined}
          {...register('email')}
        />
        {errors.email && (
          <p id="email-error" className="text-destructive text-xs">
            {errors.email.message}
          </p>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="password">Lozinka</Label>
        <input
          id="password"
          type="password"
          autoComplete="current-password"
          className="border-input bg-background focus-visible:ring-ring rounded-md border px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2"
          aria-invalid={Boolean(errors.password)}
          aria-describedby={errors.password ? 'password-error' : undefined}
          {...register('password')}
        />
        {errors.password && (
          <p id="password-error" className="text-destructive text-xs">
            {errors.password.message}
          </p>
        )}
      </div>

      {serverError && (
        <div role="alert" className="bg-destructive/10 text-destructive rounded-md p-3 text-sm">
          {serverError}
        </div>
      )}

      <Button type="submit" disabled={disabled}>
        {disabled ? 'Prijavljujem...' : 'Prijavi se'}
      </Button>

      <p className="text-muted-foreground text-sm">
        Nemaš nalog?{' '}
        <Link href="/registracija" className="text-primary underline underline-offset-4">
          Registruj se
        </Link>
      </p>
    </form>
  )
}
